// Abrechnungs-Wizard (DCP-2228, 08.09.2026; Schritt 3 "Nachweis" DCP-2228
// follow-up 15.09.2026)
//
// Vier Schritte in einem Dialog, aber KEIN EventImportModal-Fall ("mehr Bauwerk
// als Nutzen") — anders als dort haben die Schritte hier unterschiedliche
// Datenquellen (ticket-api-Refund, Teilnehmerliste, Produktsuche, Nachweis-
// Upload) und je eigene Validierung/Sperre, und Schritt 4 braucht eine
// serverseitige Vorschau-Berechnung, bevor er etwas anzeigen kann — das
// rechtfertigt echte Schritt-Navigation.
//
// Der Server bleibt die einzige Quelle der Wahrheit für die Beträge (Schritt 4
// zeigt nur, was /refund/preview liefert) und für die Nachweis-Pflicht
// (view.proofRequired kommt aus /refund, nicht aus einer lokalen Neuberechnung)
// — im Frontend wird nichts nachgerechnet.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Search, X, Download } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ParticipantsTable, type ParticipantRow } from './ParticipantsTable';
import { Input } from '@/components/ui/input';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import {
  useRefund,
  useParticipants,
  useStartRefund,
  useSaveRefundDraft,
  useSubmitRefund,
  usePreviewRefund,
  useSearchWineProducts,
  useResolveWineTaxCategory,
  useAddRefundParticipant,
  useCancelRefundTicket,
  useUploadRefundProof,
  useDeleteRefundProof,
  useApproveRefundProof,
  useTransferRefund,
} from '@/services/event-service';
import { formatCurrency } from '@/lib/i18n/formatters';
import { toPriceFromGross, vatAmount } from '@/lib/price';
import { apiClient } from '@/lib/api-client';
import { useToast } from '@/hooks/use-toast';
import { useMe } from '@/hooks/usePermissions';
import { Stepper } from '@/components/ui/stepper';
import { EventAudience, Role, TAX_CATEGORY_PERCENT } from '@/types';
import type {
  DepotEvent,
  Participant,
  Price,
  RefundDraftParticipant,
  RefundDraftWine,
  Settlement,
  WineProduct,
} from '@/types';

/**
 * Ein Ticket gilt als storniert, wenn keiner der drei Status gesetzt ist
 * (moved 25.09.2026 from the removed ParticipantsDialog.tsx).
 *
 * Abgeleitet, weil die ticket-api keinen Storno-Status kennt: ein
 * zurückgegebenes Ticket behält seinen Datensatz und verliert nur die Flags.
 *
 * `redeemed` zählt mit — wer schon eingelöst hat, war da, und den aus der
 * Anwesenheitsliste zu streichen wäre schlicht falsch.
 */
export function isCancelled(p: Pick<Participant, 'sold' | 'reserved' | 'redeemed' | 'cancelled'>): boolean {
  // Das Soft-Storno-Flag der ticket-api (cancelled) gewinnt; die Flag-Ableitung bleibt
  // als Rückfall für ältere Antworten ohne das Feld.
  return p.cancelled === true || (!p.sold && !p.reserved && !p.redeemed);
}


interface Props {
  /** null = geschlossen; sonst die VA, für die die Abrechnung geöffnet wird. */
  event: DepotEvent | null;
  onOpenChange: (open: boolean) => void;
}

/** Preis in € formatiert, aus einer Price (netto+brutto) — Netto prominent oben
 *  (das ist der maßgebliche Betrag), Brutto+Steuersatz sekundär darunter
 *  (21.09.2026-Follow-up: vorher umgekehrt). */
function PriceCell({ price, taxLabel }: { price: Price; taxLabel: string }) {
  return (
    <div className="text-right text-sm">
      <div className="font-medium">
        {formatCurrency(price.netto.centAmount / 100, price.netto.currencyCode)}
      </div>
      <div className="text-xs text-muted-foreground">
        {formatCurrency(price.brutto.centAmount / 100, price.brutto.currencyCode)} · {taxLabel}
      </div>
    </div>
  );
}

export function RefundWizardDialog({ event, onOpenChange }: Props) {
  const { t } = useTranslation('events');
  const eventId = event?.identifier ?? null;

  // Same source as EventListPage.tsx's role check — /api/me, not useAuth()
  // (that context only ever carries the raw MSAL account, never a resolved
  // role). Admin/Zentrale sees the Stornieren/Löschen-Icon on every row;
  // Depot-Partner only on manually nachgepflegte Zeilen (see the icon's
  // `disabled`/visibility logic below).
  const { data: me } = useMe();
  const role = me?.role as Role | undefined;
  const isPartner = role === Role.Partner || role === Role.Mitarbeiter;
  // Nachtragen (Named + Anzahl-Sammelerfassung) nur für Admin (22.09.2026-
  // Follow-up): für geschlossene Gesellschaften übernimmt jetzt die
  // automatische Ticket-Erstellung bei Freigabe/Kontingent-Änderung diese
  // Rolle; Partner/Zentrale sollen nicht mehr selbst nachtragen können.
  const isAdmin = role === Role.Admin;

  const { data: view, isLoading: viewLoading } = useRefund(eventId);
  const { data: participantsData } = useParticipants(eventId);
  const startMut = useStartRefund();
  const saveDraftMut = useSaveRefundDraft();
  const submitMut = useSubmitRefund();
  const previewMut = usePreviewRefund();
  const searchMut = useSearchWineProducts();
  const resolveTaxMut = useResolveWineTaxCategory();
  const addParticipantMut = useAddRefundParticipant();
  const cancelTicketMut = useCancelRefundTicket();
  const uploadProofMut = useUploadRefundProof();
  const deleteProofMut = useDeleteRefundProof();
  const approveProofMut = useApproveRefundProof();
  const transferMut = useTransferRefund();
  const { toast } = useToast();

  const [step, setStep] = useState<1 | 2 | 3 | 4>(1); // 1 Teilnehmer, 2 Weine, 3 Nachweis, 4 Zusammenfassung
  const [participants, setParticipants] = useState<RefundDraftParticipant[]>([]);
  const [wines, setWines] = useState<RefundDraftWine[]>([]);
  const [addingSku, setAddingSku] = useState<string | null>(null);
  const [wineQuery, setWineQuery] = useState('');
  const [searchResults, setSearchResults] = useState<WineProduct[]>([]);
  const [settlement, setSettlement] = useState<Settlement | null>(null);
  const [seeded, setSeeded] = useState(false);
  /** Anzahl-Sammelerfassung, geschlossene Gesellschaft (21.09.2026-Follow-up,
   *  einziger Nachpflege-Weg seit 25.09.2026) — je ein Aufruf pro Person, ohne Namen. */
  const [anonymousCount, setAnonymousCount] = useState(1);
  const [addingAnonymous, setAddingAnonymous] = useState(false);
  /** Ticket pending the "Stornieren"-Bestätigung; null = kein Dialog offen. */
  const [cancelTarget, setCancelTarget] = useState<string | null>(null);
  /** Nachweis-Löschen-Bestätigung offen? (21.09.2026-Follow-up) */
  const [confirmDeleteProof, setConfirmDeleteProof] = useState(false);
  const [downloadingProof, setDownloadingProof] = useState(false);
  const proofInputRef = useRef<HTMLInputElement>(null);
  /** Steuert nur die Bestätigungstexte — die eigentliche Cancel-vs-Delete-Entscheidung trifft der Server. */

  const refund = view?.refund ?? null;
  const readOnly = refund !== null && refund.status !== 'new';
  const proofRequired = view?.proofRequired ?? false;
  const proof = view?.proof ?? null;
  const settlementMode = view?.settlementMode ?? 'manual';
  /** VAM hat den Nachweis bereits geprüft/freigegeben (DCP-2228 follow-up, 16.09.2026) — zeigt sich am Status, der 'in_review_proof' hinter sich hat. */
  const proofApproved =
    proofRequired && refund !== null && refund.status !== 'new' && refund.status !== 'in_review_proof';

  // Reset per-event local state whenever a different event's wizard opens.
  useEffect(() => {
    setStep(1);
    setSettlement(null);
    setSeeded(false);
    setCancelTarget(null);
  }, [eventId]);

  // Seeds the editable state exactly once per opening: from the saved draft if
  // one exists, otherwise from the real ticket-api participant list (default
  // included = not cancelled — cancelled tickets are unlikely to have been
  // attended, but stay togglable since a re-instated ticket is possible).
  //
  // Bug fix (22.09.2026-Follow-up): "Abrechnung starten" always creates the
  // draft via emptyDraft() (participants: []) — a *truthy* but still-pristine
  // object, not evidence that someone already saved a participant list. Using
  // plain `view.draft` truthiness here treated that pristine draft as
  // "already seeded" and never fell through to the real ticket-api list, so
  // every freshly started Abrechnung showed zero participants even though
  // tickets had sold. A draft only counts as seeded once it actually carries
  // participant rows.
  useEffect(() => {
    if (seeded || !view) return;
    if (view.draft && view.draft.participants.length > 0) {
      // Ohne Checkbox gibt es keinen Weg mehr, jemanden aus der Zählung zu nehmen —
      // ein früher abgewählter, nicht stornierter Teilnehmer würde sonst unsichtbar fehlen.
      setParticipants(view.draft.participants.map((p) => (p.cancelled ? p : { ...p, included: true })));
      setWines(view.draft.wines);
      setSeeded(true);
    } else if (participantsData) {
      // A pristine draft (see above) still carries whatever wines it has —
      // already empty in practice at this point, but reading it here (instead
      // of leaving the initial useState value) means nothing is silently
      // dropped if that ever stops being true.
      setWines(view.draft?.wines ?? []);
      setParticipants(
        participantsData.items.map((p) => ({
          ticketId: p.identifier,
          // No name/email resolution anymore (25.09.2026 — the ticket-api's
          // Participants API is gone) — the table shows the customer number
          // or "Anonym", never a name. Freshly seeded from a real ticket sale,
          // never manually added — a Nachpflege entry always exists in a
          // draft the moment it is created, never only as a "phantom" ticket.
          customerNumber: p.customerNumber,
          reserved: p.reserved,
          sold: p.sold,
          redeemed: p.redeemed,
          cancelled: p.cancelled === true,
          included: !isCancelled(p),
        }))
      );
      setSeeded(true);
    }
  }, [view, participantsData, seeded, t]);

  if (!event) return null;

  // Zeilen für die gemeinsame Teilnehmertabelle. Storniert = in der Abrechnung storniert
  // ODER bei der ticket-api alle drei Flags verloren (siehe isCancelled); strikt auf false
  // geprüft, weil ältere Entwürfe die Flags nicht kennen (undefined) und nicht als storniert gelten.
  const participantRows: ParticipantRow[] = participants.map((p) => {
    const cancelled =
      p.cancelled === true || (p.reserved === false && p.sold === false && p.redeemed === false);
    // Admin/Zentrale storniert oder löscht jede Zeile; Depot-Partner nur die eigenen manuell
    // nachgepflegten — echte Ticketkäufe bleiben für sie unantastbar (11.09.2026).
    const canRemove = !isPartner || p.manuallyAdded;
    return {
      id: p.ticketId,
      label:
        p.name ??
        (p.customerNumber
          ? `${t('refund.participants.customerNumber')} ${p.customerNumber}`
          : t('participants.anonymous')),
      sublabel: p.email,
      notes: p.manuallyAdded ? [t('participants.manuallyAdded')] : undefined,
      reserved: !!p.reserved,
      sold: !!p.sold,
      redeemed: !!p.redeemed,
      cancelled,
      action:
        !readOnly && canRemove
          ? {
              title: cancelled
                ? t('participants.cancelled')
                : t('refund.participants.cancelTicket'),
              onClick: () => setCancelTarget(p.ticketId),
            }
          : undefined,
    };
  });

  async function persistDraft(next: {
    participants?: RefundDraftParticipant[];
    wines?: RefundDraftWine[];
  }) {
    if (!eventId) return;
    await saveDraftMut.mutateAsync({
      eventId,
      draft: {
        participants: next.participants ?? participants,
        wines: next.wines ?? wines,
      },
    });
  }

  /**
   * Anzahl-Sammelerfassung für geschlossene Gesellschaften (21.09.2026-Follow-up;
   * einziger Nachpflege-Weg seit 25.09.2026 — die benannte Nachpflege entfiel mit
   * dem Participants-API der ticket-api): `anonymousCount`-mal nacheinander
   * (nicht parallel — jeder Aufruf liest/schreibt denselben Server-Entwurf, siehe
   * createAddRefundParticipantHandler). Das letzte Ergebnis trägt bereits den
   * vollständig akkumulierten Entwurf.
   */
  async function handleAddAnonymous() {
    if (!eventId || anonymousCount < 1) return;
    await persistDraft({ participants });
    setAddingAnonymous(true);
    try {
      let result;
      for (let i = 0; i < anonymousCount; i++) {
        result = await addParticipantMut.mutateAsync({ eventId, input: { anonymous: true } });
      }
      if (result?.draft) setParticipants(result.draft.participants);
      setAnonymousCount(1);
    } finally {
      setAddingAnonymous(false);
    }
  }

  async function handleConfirmCancel() {
    if (!eventId || !cancelTarget) return;
    // Same reason as handleAddAnonymous()'s persistDraft() call above: the
    // initial ticket-sale seed only ever lands in local state. Without this,
    // a session's first Stornieren/Löschen finds no draft server-side —
    // createCancelRefundTicketHandler then has nothing to update, its
    // response's `draft` stays null, and the list silently keeps showing the
    // stale (pre-cancel) state until a full reload re-seeds it.
    await persistDraft({ participants });
    const result = await cancelTicketMut.mutateAsync({ eventId, ticketId: cancelTarget });
    if (result.draft) setParticipants(result.draft.participants);
  }

  async function handleSearch() {
    const query = wineQuery.trim();
    if (!query) return;
    const results = await searchMut.mutateAsync(query);
    setSearchResults(results);
  }

  function clearWineSearch() {
    setWineQuery('');
    setSearchResults([]);
  }

  async function addWine(product: WineProduct) {
    if (wines.some((w) => w.sku === product.sku)) return;
    setAddingSku(product.sku);
    let taxCategory = product.taxCategory; // Default aus der Suche — die Rückfallebene
    try {
      taxCategory = await resolveTaxMut.mutateAsync(product.sku);
    } catch {
      // resolveWineTaxCategory fällt serverseitig schon selbst zurück (siehe
      // backend/src/events/products/tax-category-resolver.ts) — landet dieser
      // catch hier, war es ein Transport-/Auth-Fehler zum admin-app-Backend,
      // kein CT-Lookup-Miss. "Hinzufügen" trotzdem nicht blockieren.
    } finally {
      setAddingSku(null);
    }
    const updated = [
      ...wines,
      {
        sku: product.sku,
        name: product.name,
        qtyUsed: 1,
        priceGrossCents: product.priceGrossCents,
        currencyCode: product.currencyCode,
        taxCategory,
      },
    ];
    setWines(updated);
    void persistDraft({ wines: updated });
  }

  function setWineQty(sku: string, qty: number) {
    const updated = wines.map((w) => (w.sku === sku ? { ...w, qtyUsed: Math.max(1, qty) } : w));
    setWines(updated);
    void persistDraft({ wines: updated });
  }

  function removeWine(sku: string) {
    const updated = wines.filter((w) => w.sku !== sku);
    setWines(updated);
    void persistDraft({ wines: updated });
  }

  async function goToStep(target: 1 | 2 | 3 | 4) {
    setStep(target);
    if (target === 4 && eventId) {
      const result = await previewMut.mutateAsync(eventId);
      setSettlement(result);
    }
  }

  async function handleUploadProof(file: File) {
    if (!eventId) return;
    await uploadProofMut.mutateAsync({ eventId, file });
  }

  async function handleDownloadProof() {
    if (!eventId) return;
    setDownloadingProof(true);
    try {
      const blob = await apiClient.getRefundProofBlob(eventId);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Erst nach dem Öffnen freigeben — ein sofortiges revokeObjectURL könnte
      // den neuen Tab mit einer ungültigen URL laden lassen.
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } finally {
      setDownloadingProof(false);
    }
  }

  /** Nachweis entfernen, ohne einen neuen hochzuladen (21.09.2026-Follow-up). */
  async function handleConfirmDeleteProof() {
    if (!eventId) return;
    await deleteProofMut.mutateAsync(eventId);
    setConfirmDeleteProof(false);
  }

  /** Freigabe durch Admin/Zentrale (21.09.2026-Follow-up, "Abrechnung"-Spalte). */
  async function handleApproveProof() {
    if (!eventId) return;
    await approveProofMut.mutateAsync(eventId);
  }

  /**
   * "Abrechnung freigeben & übertragen" (DCP-2228 follow-up, 16.09.2026) —
   * nur erreichbar, solange settlementMode automatisch ist (siehe die
   * settlementMode==='manual'-Sperre weiter oben, die den ganzen Wizard
   * inklusive Footer ersetzt); die serverseitige Sperre
   * (requireAutomaticSettlementMode, refund-handlers.ts) bleibt trotzdem die
   * eigentliche Instanz.
   */
  async function handleTransfer() {
    if (!eventId) return;
    try {
      await transferMut.mutateAsync(eventId);
      toast({ title: t('refund.transferSuccess') });
    } catch (e) {
      toast({
        title: t('toast.error'),
        description: e instanceof Error ? e.message : String(e),
        variant: 'destructive',
      });
    }
  }

  async function handleSubmit() {
    if (!eventId) return;
    await submitMut.mutateAsync(eventId);
  }

  return (
    <>
      <Dialog open={event !== null} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[52.8rem]">
          <DialogHeader>
            <DialogTitle>{t('refund.title')}</DialogTitle>
            <DialogDescription>{event.title}</DialogDescription>
          </DialogHeader>

          {viewLoading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">{t('refund.loading')}</p>
          ) : settlementMode === 'manual' ? (
            // Für eine VA vor dem 01.02.2027-Stichtag darf der neue Wizard gar
            // nicht erst betreten werden (DCP-2228 follow-up, 16.09.2026,
            // Nutzerentscheidung "vor Start der Abrechnung") — die
            // Berechnungslogik hier (Pauschale-Matching, Weinkostenübernahme)
            // basiert auf einem anderen Vergütungsmodell als die alte,
            // manuelle Abrechnung. Gilt auch für eine schon laufende
            // Abrechnung ("einfrieren"), unabhängig vom Fortschritt — die
            // serverseitige Sperre (requireAutomaticSettlementMode,
            // refund-handlers.ts) ist ohnehin die eigentliche Instanz. Weiter/
            // Submit/Transfer/ApproveProof erscheinen deshalb erst gar nicht.
            <div className="space-y-2 py-4">
              <p className="text-sm font-medium">{t('refund.manualModeHint')}</p>
              <p className="text-sm text-muted-foreground">{t('refund.manualModeExplainer')}</p>
            </div>
          ) : !refund ? (
            // Noch keine Abrechnung angelegt — nur der Start-Button, keine
            // Schritt-Navigation (es gibt noch nichts, wohin man springen könnte).
            // Sobald startMut erfolgreich war, füllt sein onSuccess die
            // refund-Query direkt (useStartRefund, event-service.ts), `refund`
            // wird also ohne Umweg über einen Refetch nicht-null — `step` steht
            // schon auf 1 (Teilnehmer), der nächste Render zeigt damit von selbst
            // Schritt 1 statt hier stehen zu bleiben.
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">{t('refund.startExplainer')}</p>
              <Button onClick={() => void startMut.mutateAsync(eventId!)} disabled={startMut.isPending}>
                {t('refund.start')}
              </Button>
            </div>
          ) : (
            <>
              {/* Schritt-Navigation (Stepper-Optik 12.10.2026 nach Artifact-
                  Vorlage) — kein Sequenzzwang, jeder Schritt bleibt über
                  seinen onClick jederzeit anklickbar. */}
              <div className="border-b pb-3">
                <Stepper
                  steps={([1, 2, 3, 4] as const).map((n) => ({
                    label: t(`refund.step${n}`),
                    onClick: () => void goToStep(n),
                  }))}
                  activeIndex={step - 1}
                />
              </div>

              {step === 1 ? (
                <div className="max-h-[66vh] space-y-6 overflow-y-auto py-4">
                  <section>
                    <h3 className="mb-2 text-sm font-medium">{t('refund.participants.title')}</h3>
                    <ParticipantsTable rows={participantRows} showActions={!readOnly} emptyText={t('participants.empty')} />
                  </section>

                  {/* Nachpflege ist seit 25.09.2026 anonym-only (die benannte
                      Erfassung entfiel mit dem Participants-API der
                      ticket-api) — nur bei geschlossener Gesellschaft
                      (createAddRefundParticipantHandler's eigenes Gate),
                      unabhängig von proofRequired: das war vorher eine
                      zusätzliche, engere UI-Einschränkung, die ohne den
                      benannten Weg als Alternative keinen Sinn mehr ergibt. */}
                  {!readOnly && isAdmin && event.audience === EventAudience.Closed && (
                    <section>
                      <h3 className="mb-2 text-sm font-medium">{t('refund.participants.addTitle')}</h3>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">
                          {t('refund.participants.addAnonymousLabel')}
                        </span>
                        <Input
                          type="number"
                          min={1}
                          className="w-20"
                          value={anonymousCount}
                          onChange={(e) => setAnonymousCount(Math.max(1, Number(e.target.value)))}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          className="gap-2"
                          disabled={anonymousCount < 1 || addingAnonymous}
                          onClick={() => void handleAddAnonymous()}
                        >
                          <Plus className="h-4 w-4" />
                          {t('refund.participants.addAnonymous')}
                        </Button>
                      </div>
                    </section>
                  )}
                </div>
              ) : step === 2 ? (
                <div className="max-h-[66vh] space-y-6 overflow-y-auto py-4">
                  {/* Nur noch Anzeige (16.09.2026-Follow-up) — die Frage selbst
                      wird jetzt in der VA-Eingabe unter "Finanzielle
                      Unterstützung" gepflegt (EventFormDialog.tsx), weil sie
                      eine Tatsache über die VA ist, keine Abrechnungs-Arbeit.
                      Der Wert bleibt für die Weinkosten-Voraussetzung
                      relevant, siehe computeSettlement() im Backend. */}
                  <section>
                    <p className="text-sm text-muted-foreground">
                      {event.glassOrTableSale
                        ? t('refund.glassOrTableSaleYes')
                        : t('refund.glassOrTableSaleNo')}
                    </p>
                  </section>

                  <section>
                    <h3 className="mb-2 text-sm font-medium">{t('refund.wines.title')}</h3>
                    {!readOnly && (
                      <div className="mb-3 flex gap-2">
                        <Input
                          placeholder={t('refund.wines.searchPlaceholder')}
                          value={wineQuery}
                          onChange={(e) => setWineQuery(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && void handleSearch()}
                        />
                        <Button type="button" variant="outline" onClick={() => void handleSearch()}>
                          <Search className="h-4 w-4" />
                        </Button>
                        {(wineQuery || searchResults.length > 0) && (
                          <Button
                            type="button"
                            variant="outline"
                            title={t('refund.wines.clearSearch')}
                            onClick={clearWineSearch}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    )}
                    {searchResults.length > 0 && !readOnly && (
                      <div className="mb-3 rounded-md border">
                        {searchResults.map((product) => (
                          <div
                            key={product.sku}
                            className="flex items-center justify-between border-b px-2 py-1.5 last:border-b-0"
                          >
                            <div className="text-sm">
                              <div>{product.name}</div>
                              <div className="text-xs text-muted-foreground">{product.sku}</div>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              disabled={addingSku === product.sku}
                              onClick={() => void addWine(product)}
                            >
                              <Plus className="h-4 w-4" />
                              {t('refund.wines.add')}
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="rounded-md border">
                      <table className="w-full text-sm">
                        <tbody>
                          {wines.map((w) => {
                            const price = toPriceFromGross(w.priceGrossCents, w.currencyCode, w.taxCategory);
                            const vat = vatAmount(price);
                            return (
                              <tr key={w.sku} className="border-b last:border-b-0">
                                <td className="py-1.5 pl-2">
                                  <div>{w.name}</div>
                                  <div className="text-xs text-muted-foreground">{w.sku}</div>
                                </td>
                                <td className="w-20 py-1.5">
                                  <Input
                                    type="number"
                                    min={1}
                                    value={w.qtyUsed}
                                    disabled={readOnly}
                                    onChange={(e) => setWineQty(w.sku, Number(e.target.value))}
                                  />
                                </td>
                                <td className="py-1.5 pr-2 text-right text-xs">
                                  <div>
                                    {t('refund.wines.priceGross')}:{' '}
                                    {formatCurrency(price.brutto.centAmount / 100, price.brutto.currencyCode)}
                                  </div>
                                  <div className="text-muted-foreground">
                                    {t('refund.wines.priceNet')}:{' '}
                                    {formatCurrency(price.netto.centAmount / 100, price.netto.currencyCode)}
                                  </div>
                                  <div className="text-muted-foreground">
                                    {t('refund.wines.vat')} ({TAX_CATEGORY_PERCENT[w.taxCategory]}
                                    %): {formatCurrency(vat.centAmount / 100, vat.currencyCode)}
                                  </div>
                                </td>
                                {!readOnly && (
                                  <td className="w-10 py-1.5 pr-2 text-right">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="text-destructive hover:text-destructive"
                                      onClick={() => removeWine(w.sku)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                          {wines.length === 0 && (
                            <tr>
                              <td
                                colSpan={readOnly ? 3 : 4}
                                className="py-3 text-center text-muted-foreground"
                              >
                                {t('refund.wines.empty')}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </section>
                </div>
              ) : step === 3 ? (
                <div className="max-h-[66vh] space-y-4 overflow-y-auto py-4">
                  {proofApproved && (
                    <p className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                      {t('refund.proofApprovedHint')}
                    </p>
                  )}
                  {!proofRequired ? (
                    <p className="text-sm text-muted-foreground">{t('refund.proof.notRequired')}</p>
                  ) : (
                    <div className="space-y-3">
                      {proof ? (
                        <div className="flex items-center justify-between rounded-md border p-3 text-sm">
                          <span>{t('refund.proof.current', { filename: proof.filename })}</span>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="gap-2"
                              disabled={downloadingProof}
                              onClick={() => void handleDownloadProof()}
                            >
                              <Download className="h-4 w-4" />
                              {t('refund.proof.download')}
                            </Button>
                            {!readOnly && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="gap-2 text-destructive hover:text-destructive"
                                disabled={deleteProofMut.isPending}
                                onClick={() => setConfirmDeleteProof(true)}
                              >
                                <Trash2 className="h-4 w-4" />
                                {t('refund.proof.delete')}
                              </Button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">{t('refund.proof.missing')}</p>
                      )}
                      {!readOnly && (
                        <>
                          <input
                            ref={proofInputRef}
                            type="file"
                            accept="application/pdf"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              e.target.value = '';
                              if (file) void handleUploadProof(file);
                            }}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            disabled={uploadProofMut.isPending}
                            onClick={() => proofInputRef.current?.click()}
                          >
                            {proof ? t('refund.proof.replace') : t('refund.proof.uploadLabel')}
                          </Button>
                          {uploadProofMut.isError && (
                            <p className="text-xs text-destructive">
                              {uploadProofMut.error instanceof Error && uploadProofMut.error.message.includes('PDF')
                                ? t('refund.proof.invalidType')
                                : t('refund.proof.uploadError')}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              ) : previewMut.isPending || !settlement ? (
                <p className="py-8 text-center text-sm text-muted-foreground">{t('refund.loading')}</p>
              ) : (
                <div className="max-h-[66vh] space-y-4 overflow-y-auto py-4">
                  {proofApproved && (
                    <p className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                      {t('refund.proofApprovedHint')}
                    </p>
                  )}
                  {!settlement.winesQualify && (
                    <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-800">
                      {t('refund.wines.notQualified')}
                    </p>
                  )}
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-xs text-muted-foreground">
                        <th className="py-1">{t('refund.summary.position')}</th>
                        <th className="py-1 text-right">{t('refund.summary.charge')}</th>
                        <th className="py-1 text-right">{t('refund.summary.refund')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-1.5">{t('refund.summary.flatRate')}</td>
                        <td />
                        <td>
                          <PriceCell
                            price={settlement.flatRate}
                            taxLabel={t(`taxCategory.${settlement.flatRateTaxCategory}`)}
                          />
                        </td>
                      </tr>
                      {settlement.wines.map((w) => (
                        <tr key={w.sku} className="border-b">
                          <td className="py-1.5">
                            <div>{w.name}</div>
                            <div className="text-xs text-muted-foreground">
                              <div>{t('refund.summary.qtyUsed', { count: w.qtyUsed })}</div>
                              <div>{t('refund.summary.qtyRefunded', { count: w.qtyEligibleHalfBottles / 2 })}</div>
                            </div>
                          </td>
                          <td>
                            <PriceCell price={w.charge} taxLabel={t(`taxCategory.${w.taxCategory}`)} />
                          </td>
                          <td>
                            <PriceCell price={w.refund} taxLabel={t(`taxCategory.${w.taxCategory}`)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td className="pt-2 font-medium">{t('refund.summary.finalAmount')}</td>
                        <td />
                        <td className="pt-2">
                          <PriceCell
                            price={settlement.finalAmount}
                            taxLabel={t(`taxCategory.${settlement.flatRateTaxCategory}`)}
                          />
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </>
          )}

          {settlementMode !== 'manual' && (
            <DialogFooter>
              {/* Admin/Zentrale prüft den Nachweis (Step 3) und/oder die
                  Zusammenfassung (Step 4), beide schon ohne readOnly-Sperre
                  erreichbar — die Freigabe selbst ist eine Ein-Klick-Aktion,
                  unabhängig vom gerade offenen Schritt (21.09.2026-Follow-up). */}
              {!isPartner && refund?.status === 'in_review_proof' && (
                <Button
                  type="button"
                  className="gap-2 bg-orange-500 text-white hover:bg-orange-600"
                  disabled={approveProofMut.isPending}
                  onClick={() => void handleApproveProof()}
                >
                  {t('refund.approveProof')}
                </Button>
              )}
              {/* "Buchhaltung" ist vorerst die Rolle Zentrale, siehe requireCentral
                  auf der Route (DCP-2228 follow-up, 16.09.2026). Nur erreichbar,
                  solange settlementMode automatisch ist — im manuellen Fall zeigt
                  der Wizard oben ohnehin nur den Block-Hinweis, kein Footer. */}
              {!isPartner && refund?.status === 'in_review' && (
                <Button
                  type="button"
                  className="gap-2 bg-orange-500 text-white hover:bg-orange-600"
                  disabled={transferMut.isPending}
                  onClick={() => void handleTransfer()}
                >
                  {t('refund.transferAndApprove')}
                </Button>
              )}
              {refund && step < 4 && (
                <Button
                  type="button"
                  disabled={step === 3 && proofRequired && !proof}
                  onClick={() => void goToStep((step + 1) as 2 | 3 | 4)}
                >
                  {t('refund.next')}
                </Button>
              )}
              {step === 4 && !readOnly && refund && (
                <Button
                  onClick={() => void handleSubmit()}
                  disabled={submitMut.isPending || (proofRequired && !proof)}
                >
                  {t('refund.submit')}
                </Button>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={cancelTarget !== null}
        onOpenChange={(open) => !open && setCancelTarget(null)}
        title={t('refund.participants.cancelConfirmTitle')}
        description={t('refund.participants.cancelConfirmDescription')}
        confirmLabel={t('refund.participants.cancelTicket')}
        cancelLabel={t('actions.cancel')}
        variant="destructive"
        onConfirm={() => void handleConfirmCancel()}
      />

      <ConfirmationDialog
        open={confirmDeleteProof}
        onOpenChange={setConfirmDeleteProof}
        title={t('refund.proof.deleteConfirmTitle')}
        description={t('refund.proof.deleteConfirmDescription')}
        confirmLabel={t('refund.proof.delete')}
        cancelLabel={t('actions.cancel')}
        variant="destructive"
        onConfirm={() => void handleConfirmDeleteProof()}
      />
    </>
  );
}
